/**
 * Interfaces TypeScript para el módulo de Auditoría de Ventas Home
 */

/**
 * Usuario simplificado en auditoría
 */
export interface AuditoriaUsuario {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  nombre_completo: string;
}

/**
 * Cambio de un campo
 */
export interface CambioCampo {
  anterior: any;
  nuevo: any;
}

/**
 * Diccionario de cambios
 */
export type CambiosDict = Record<string, CambioCampo>;

/**
 * Módulos disponibles
 */
export type ModuloAuditoria = 'PROSPECTO' | 'PREVENTA' | 'PREAGENDAMIENTO' | 'AGENDAMIENTO' | 'CLIENTE' | 'CONTRATO' | 'ORDEN_TRABAJO';

/**
 * Acciones disponibles
 */
export type AccionAuditoria =
  | 'CREAR'
  | 'ACTUALIZAR'
  | 'ELIMINAR'
  | 'APROBAR'
  | 'RECHAZAR'
  | 'ACEPTAR'
  | 'FIRMAR'
  | 'CARGAR_DOCUMENTO'
  | 'CAMBIAR_ESTADO'
  | 'ASIGNAR'
  | 'REASIGNAR'
  | 'CANCELAR'
  | 'CONSULTAR';

/**
 * Auditoría de ventas home - Vista de lista
 */
export interface AuditoriaVentasHome {
  id_bitacora: string;
  usuario: number | null;
  usuario_nombre: string | null;
  usuario_info: AuditoriaUsuario | null;
  modulo: ModuloAuditoria;
  accion: AccionAuditoria;
  descripcion: string | null;
  endpoint: string | null;
  metodo_http: string | null;
  estado_http: number | null;
  registro_id: string | null;
  registro_codigo: string | null;
  registro_tipo: string | null;
  ip_address: string | null;
  fecha_hora: string;
  fecha_hora_local: string | null;
  tiene_cambios: boolean;
}

/**
 * Auditoría de ventas home - Vista detallada
 */
export interface AuditoriaVentasHomeDetalle extends AuditoriaVentasHome {
  prospecto: string | null;
  prospecto_codigo: string | null;
  preventa: string | null;
  preventa_codigo: string | null;
  preagendamiento: string | null;
  preagendamiento_codigo: string | null;
  agendamiento: string | null;
  agendamiento_codigo: string | null;
  cliente: string | null;
  cliente_codigo: string | null;
  datos_anteriores: Record<string, any>;
  datos_nuevos: Record<string, any>;
  cambios: CambiosDict;
  request_body: Record<string, any>;
  response_body: Record<string, any>;
  user_agent: string | null;
  observaciones: string | null;
  metadata: Record<string, any>;
}

/**
 * Filtros para búsqueda de auditoría
 */
export interface AuditoriaFiltros {
  modulo?: ModuloAuditoria;
  accion?: AccionAuditoria;
  usuario_id?: number;
  usuario_nombre?: string;
  registro_id?: string;
  registro_codigo?: string;
  fecha_desde?: string;
  fecha_hasta?: string;
  fecha?: string;
  prospecto_id?: string;
  preventa_id?: string;
  agendamiento_id?: string;
  cliente_id?: string;
  search?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

/**
 * Respuesta paginada
 */
export interface AuditoriaPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: AuditoriaVentasHome[];
}

/**
 * Estadísticas de auditoría
 */
export interface AuditoriaEstadisticas {
  periodo_dias: number;
  total_registros: number;
  por_modulo: { modulo: string; total: number }[];
  por_accion: { accion: string; total: number }[];
  por_usuario: { usuario_nombre: string; total: number }[];
  ultimos_registros: AuditoriaVentasHome[];
}

/**
 * Opción de select
 */
export interface SelectOption {
  value: string;
  label: string;
}

/**
 * Usuario con total de acciones
 */
export interface UsuarioAuditoria {
  id: number;
  username: string;
  nombre: string;
  total_acciones: number;
}

/**
 * Colores por acción
 */
export const ACCION_COLORS: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  CREAR: 'success',
  ACTUALIZAR: 'info',
  ELIMINAR: 'error',
  APROBAR: 'success',
  RECHAZAR: 'error',
  ACEPTAR: 'success',
  FIRMAR: 'primary',
  CARGAR_DOCUMENTO: 'info',
  CAMBIAR_ESTADO: 'warning',
  ASIGNAR: 'primary',
  REASIGNAR: 'warning',
  CANCELAR: 'error',
  CONSULTAR: 'default',
};

/**
 * Colores por módulo
 */
export const MODULO_COLORS: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary'> = {
  PROSPECTO: 'primary',
  PREVENTA: 'info',
  PREAGENDAMIENTO: 'warning',
  AGENDAMIENTO: 'warning',
  CLIENTE: 'success',
  CONTRATO: 'secondary',
  ORDEN_TRABAJO: 'error',
};

/**
 * Colores por método HTTP
 */
export const METODO_HTTP_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'> = {
  GET: 'info',
  POST: 'success',
  PUT: 'warning',
  PATCH: 'warning',
  DELETE: 'error',
};

// ========================================
// Trazabilidad
// ========================================

/**
 * Etapas del flujo de ventas
 */
export type EtapaVenta = 'PROSPECTO' | 'PREVENTA' | 'PREAGENDAMIENTO' | 'AGENDAMIENTO';

export const ETAPAS_VENTA_ORDEN: EtapaVenta[] = [
  'PROSPECTO',
  'PREVENTA',
  'PREAGENDAMIENTO',
  'AGENDAMIENTO',
];

export const ETAPA_LABELS: Record<EtapaVenta, string> = {
  PROSPECTO: 'Prospecto',
  PREVENTA: 'Preventa',
  PREAGENDAMIENTO: 'Pre-Agendamiento',
  AGENDAMIENTO: 'Agendamiento',
};

/**
 * Grupo de eventos agrupados por etapa
 */
export interface TrazabilidadEtapaGrupo {
  etapa: EtapaVenta;
  eventos: AuditoriaVentasHomeDetalle[];
  estado_final: string | null;
  fecha_inicio: string | null;
  fecha_fin: string | null;
}
